import clsx from 'clsx';

export interface ScenarioCardProps {
  id?: string;
  title?: string;
  description?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  duration?: string;
  category?: string;
  imageUrl?: string;
  isLocked?: boolean;
  isSelected?: boolean;
  completedCount?: number;
  bestScore?: number;
  onSelect?: (id: string) => void;
}

const DIFFICULTY_COLORS = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-yellow-100 text-yellow-700',
  advanced: 'bg-red-100 text-red-700',
};

export function ScenarioCard({
  id = '',
  title = 'Untitled Scenario',
  description = '',
  difficulty = 'beginner',
  duration = '',
  category = '',
  imageUrl,
  isLocked = false,
  isSelected = false,
  completedCount = 0,
  bestScore,
  onSelect,
}: ScenarioCardProps) {
  // Safe difficulty color access
  const difficultyColor = DIFFICULTY_COLORS[difficulty] ?? DIFFICULTY_COLORS.beginner;

  const handleClick = () => {
    if (!isLocked && onSelect && id) {
      onSelect(id);
    }
  };

  return (
    <div
      className={clsx(
        'card overflow-hidden cursor-pointer transition-all hover:shadow-lg',
        isLocked && 'opacity-60',
        isSelected && 'ring-2 ring-primary-500'
      )}
      onClick={handleClick}
    >
      {/* Image/thumbnail area */}
      <div className="h-32 bg-gradient-to-br from-primary-400 to-primary-600 relative">
        {imageUrl ? (
          <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ScenarioIcon category={category} />
          </div>
        )}

        {/* Lock overlay */}
        {isLocked && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <LockIcon className="w-8 h-8 text-white" />
          </div>
        )}

        {/* Category badge */}
        {category && (
          <span className="absolute top-2 left-2 px-2 py-1 bg-white/90 rounded text-xs font-medium text-gray-700">
            {category}
          </span>
        )}

        {/* Selected indicator */}
        {isSelected && (
          <div className="absolute top-2 right-2">
            <CheckIcon className="w-6 h-6 text-white" />
          </div>
        )}
      </div>

      <div className="card-body">
        <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
        {description && (
          <p className="text-sm text-gray-500 line-clamp-2 mb-3">{description}</p>
        )}

        <div className="flex flex-wrap gap-2 mb-3">
          <span className={clsx('badge text-xs', difficultyColor)}>
            {difficulty}
          </span>
          {duration && (
            <span className="badge bg-gray-100 text-gray-600 text-xs">
              {duration}
            </span>
          )}
        </div>

        {/* Stats row */}
        {completedCount > 0 && (
          <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t">
            <span>Practiced {completedCount}x</span>
            {bestScore !== undefined && bestScore > 0 && (
              <span className="font-medium text-primary-600">
                Best: {bestScore}%
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ScenarioIcon({ category }: { category?: string }) {
  const safeCategory = category ?? '';
  return (
    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
      <span className="text-3xl text-white">
        {safeCategory.toLowerCase() === 'pitch' && '🎯'}
        {safeCategory.toLowerCase() === 'interview' && '💼'}
        {safeCategory.toLowerCase() === 'negotiation' && '🤝'}
        {safeCategory.toLowerCase() === 'presentation' && '📊'}
        {safeCategory.toLowerCase() === 'sales' && '💰'}
        {!['pitch', 'interview', 'negotiation', 'presentation', 'sales'].includes(safeCategory.toLowerCase()) && '🎤'}
      </span>
    </div>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}
